import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Bell, BellOff, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function CostAlerts() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAlert, setNewAlert] = useState({
    alert_type: 'threshold_exceeded',
    threshold_value: 0,
    subscription_tier: '',
    notification_email: '',
  });

  const { data: alerts, isLoading } = useQuery({
    queryKey: ['cost-alerts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_alerts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const createAlertMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('cost_alerts')
        .insert([newAlert]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-alerts'] });
      toast({ title: "Alert created successfully" });
      setShowAddForm(false);
      setNewAlert({
        alert_type: 'threshold_exceeded',
        threshold_value: 0,
        subscription_tier: '',
        notification_email: '',
      });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Failed to create alert", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const toggleAlertMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('cost_alerts')
        .update({ is_active: isActive, status: 'active' })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-alerts'] });
      toast({ title: "Alert updated" });
    },
  });

  const deleteAlertMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cost_alerts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-alerts'] });
      toast({ title: "Alert deleted" });
    },
  });

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'threshold_exceeded':
        return 'Cost Threshold';
      case 'margin_warning':
        return 'Profit Margin';
      case 'rate_limit_approaching':
        return 'AI Usage Rate';
      case 'first_customer_acquired':
        return 'First Customer';
      case 'revenue_milestone':
        return 'Revenue Milestone';
      default:
        return type;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="outline">Active</Badge>;
      case 'triggered':
        return <Badge variant="destructive">Triggered</Badge>;
      case 'resolved':
        return <Badge variant="secondary">Resolved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Cost Alerts</h2>
          <p className="text-muted-foreground">
            Configure alerts for cost thresholds, margin warnings, and milestone notifications
          </p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="mr-2 h-4 w-4" />
          New Alert
        </Button>
      </div>

      {/* Add Alert Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Alert</CardTitle>
            <CardDescription>Set up a new cost monitoring alert</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Alert Type</Label>
              <Select
                value={newAlert.alert_type}
                onValueChange={(value) => setNewAlert({ ...newAlert, alert_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="threshold_exceeded">Cost Threshold Exceeded</SelectItem>
                  <SelectItem value="margin_warning">Profit Margin Warning</SelectItem>
                  <SelectItem value="rate_limit_approaching">AI Usage Rate High</SelectItem>
                  <SelectItem value="first_customer_acquired">First Customer Acquired</SelectItem>
                  <SelectItem value="revenue_milestone">Revenue Milestone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Threshold Value</Label>
              <Input
                type="number"
                step="0.01"
                value={newAlert.threshold_value}
                onChange={(e) => setNewAlert({ ...newAlert, threshold_value: parseFloat(e.target.value) })}
                placeholder={newAlert.alert_type === 'margin_warning' ? '% below' : '$ above'}
              />
            </div>

            <div className="space-y-2">
              <Label>Subscription Tier (Optional)</Label>
              <Select
                value={newAlert.subscription_tier}
                onValueChange={(value) => setNewAlert({ ...newAlert, subscription_tier: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Tiers</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notification Email</Label>
              <Input
                type="email"
                value={newAlert.notification_email}
                onChange={(e) => setNewAlert({ ...newAlert, notification_email: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={() => createAlertMutation.mutate()}>
                Create Alert
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts List */}
      <div className="space-y-4">
        {alerts?.map((alert) => (
          <Card key={alert.id}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  {alert.is_active ? (
                    <Bell className="h-5 w-5 text-primary" />
                  ) : (
                    <BellOff className="h-5 w-5 text-muted-foreground" />
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{getAlertTypeLabel(alert.alert_type)}</h4>
                      {getStatusBadge(alert.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Threshold: {alert.alert_type === 'margin_warning' ? `${alert.threshold_value}%` : `$${alert.threshold_value}`}
                      {alert.subscription_tier && ` • ${alert.subscription_tier} tier`}
                    </p>
                    {alert.notification_email && (
                      <p className="text-xs text-muted-foreground">
                        Notify: {alert.notification_email}
                      </p>
                    )}
                    {alert.last_triggered_at && (
                      <p className="text-xs text-muted-foreground">
                        Last triggered: {new Date(alert.last_triggered_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={alert.is_active}
                    onCheckedChange={(checked) => 
                      toggleAlertMutation.mutate({ id: alert.id, isActive: checked })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteAlertMutation.mutate(alert.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {alerts?.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No alerts configured</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first alert to monitor costs and margins
                </p>
                <Button onClick={() => setShowAddForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Alert
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
